/**
 * @file 通用API请求功能
 * @author caimengyong
 */

import axios, {AxiosRequestConfig, AxiosResponse, Method} from 'axios';
import {removeNully} from '@/utils/utils';

import {unIdentify} from '@/utils/identify';
import {loginPage} from './loginPage';

const defaultOptions: AxiosRequestConfig = {
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true,

    paramsSerializer(query) {
        return Object.entries(query).reduce(
            (search, [key, value]) => {
                const part = encodeURIComponent(key) + '=' + encodeURIComponent(value as string);
                const delimiter = search ? '&' : '';
                return search + delimiter + part;
            },
            ''
        );
    },
};

const http = axios.create(defaultOptions);

interface SuccessResponse<D> {
    status: number;
    msg: string;
    toast: string;
    pageid?: string;
    data: D;
}

export interface FetchError<E> extends Error {
    responseStatus: number;
    responseData: E;
}

type Primitive = string | number | boolean | null | undefined;

interface BaseData {
    [key: string]: Primitive | Primitive[];
}

const unwrapResponse = <D>(response: AxiosResponse<SuccessResponse<D>>): D => {
    const {data} = response;

    if (data.status === 0) {
        return data.data;
    }

    const error = new Error(data.msg) as FetchError<D>;
    error.responseStatus = data.status;
    error.responseData = data.data;

    throw error;
};

export const fetch = async <O, I = void>(method: Method,
    url: string,
    rawData?: I,
    isAuth = true,
    isIdentify = false,
    customConfig?: any
): Promise<O> => {
    const config: AxiosRequestConfig = Object.assign({url, method}, customConfig);

    // 非GET方法需要改data格式和header
    const isGETMethod = method === 'GET';

    // 处理传递数据
    if (rawData) {
        // `null`或`undefined`的参数默认不发送
        const data = removeNully(rawData as any as BaseData);
        const key = isGETMethod ? 'params' : 'data';
        // 非GET方法需要JSON序列化一下
        config[key] = data;
    }

    const response = await http.request<SuccessResponse<O>>(config);

    /** 处理重定向 */
    if (isAuth) {
        // 重定向到登录页
        // 非登录状态码为 '2'
        if (response.data.status === 2) {
            loginPage({
                redirectURL: window.location.href,
            });
        }
    }
    if (isIdentify) {
        // 未认证的状态，非认证状态码为 '3'
        if (response.data.status === 3) {
            unIdentify();
        }
    }

    if (response.data.status !== 0 && response.data.toast) {
        // 暂时采用警告的方式，将错误信息输出在控制台，后期考虑上报spy平台
        console.warn(response.data.toast);
    }

    return unwrapResponse(response);
};

export const get = <O, I = void>(url: string, rawData?: I, isAuth = true, isIdentify = false): Promise<O> =>
    fetch('GET', url, rawData, isAuth, isIdentify);

// eslint-disable-next-line max-len
export const post = <O, I = void>(url: string, rawData?: I, isAuth = true, isIdentify = false, customConfig?: any): Promise<O> =>
    fetch('POST', url, rawData, isAuth, isIdentify, customConfig);
